import { useApolloClient } from "@apollo/client";
import { debounce, filter, get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useState, useMemo, useRef } from "react";

import WorkspacePropType from "src/custom-prop-types/workspace";
import { globalSearchQuery } from "src/queries/globalSearch.graphql";
import * as searchUtil from "src/util/search";
import { reportError } from "src/services/errorReporting";
import { isSearchPage } from "src/util/url";
import { matchPaths } from "src/consts/urlPaths";
import { TAGS_UI_COMPONENTS } from "src/util/consts";
import { roleBasedTagTypeIds } from "src/util/tags";
import { DEBOUNCE_SEARCH_TIME } from "src/consts/global";
import { useGlobalSearchContext } from "./GlobalSearchContext";
import SearchBox from ".";

const SEARCH_COUNT_LIMIT = 3;
const SEARCH_PAGE_COUNT_LIMIT = 20;
const PLACEHOLDER_ROWS = 3;
const SECTION_LABELS = {
  [searchUtil.SEARCH_SECTION.PEOPLE]: "People",
  [searchUtil.SEARCH_SECTION.GROUPS]: "Teams",
  [searchUtil.SEARCH_SECTION.TAGS]: "Tags",
};

const isSearchDebugEnabled = () => {
  if (typeof window === "undefined") {
    return false;
  }
  if (process.env.NODE_ENV !== "development") {
    return false;
  }
  return window.localStorage?.getItem("globalSearchDebug") === "1";
};

const emitSearchDebug = (event, payload = {}) => {
  if (!isSearchDebugEnabled()) {
    return;
  }

  const entry = {
    at: new Date().toISOString(),
    event,
    ...payload,
  };

  if (typeof window !== "undefined") {
    // Keep a bounded in-memory trace so async ordering can be inspected
    // from DevTools without relying on noisy console output.
    window.__globalSearchDebug = window.__globalSearchDebug || [];
    window.__globalSearchDebug.push(entry);
    if (window.__globalSearchDebug.length > 300) {
      window.__globalSearchDebug = window.__globalSearchDebug.slice(-300);
    }
  }
};

const getSectionPlaceholderOptions = (section) => {
  const placeholderBySection = {
    [searchUtil.SEARCH_SECTION.PEOPLE]: {
      template: "optionWithAction",
      type: "people",
    },
    [searchUtil.SEARCH_SECTION.GROUPS]: { template: "option", type: "teams" },
    [searchUtil.SEARCH_SECTION.TAGS]: { template: "optionTag", type: "tags" },
  };
  const baseValue = placeholderBySection[section];

  return Array.from({ length: PLACEHOLDER_ROWS }, (_, idx) => ({
    label: `${section}-loading-${idx}`,
    type: section,
    value: {
      ...baseValue,
      isLoading: true,
    },
  }));
};
const SearchContainer = ({
  onOptionSelect,
  isMulti = false,
  isCloseOnSelect = true,
  isStandardSelect = false,
  includePeople = true,
  includeGroups = true,
  includeTags = true,
  workspace,
  featureFlags,
  showExploreLink = false,
  tagsVisibleIn = TAGS_UI_COMPONENTS.DIRECTORY,
  dropdownOptionsDisplayLimit = 0,
  Search = SearchBox,
}) => {
  const match = matchPaths();
  const isForSearchPage = isSearchPage(match?.path);
  const [prevOptions, setPrevOptions] = useState();
  const [isFetchingAsyncQuery, setIsFetchingAsyncQuery] = useState(false);
  const [searchError, setSearchError] = useState();
  const client = useApolloClient();
  const groupTypes = get(workspace, "config.groupTypes", {});
  const customer = get(workspace, "config.customer");
  const tagTypesConfig = get(workspace, "config.tagConfig");

  const {
    defaultSearchScope,
    setDefaultSearchScope,
    setSearchResults,
    setSearchData,
  } = useGlobalSearchContext();
  const activeSearchRequestRef = useRef(0);
  const activeLoadKeyRef = useRef("");
  const activeInputValueRef = useRef("");
  const lastCompletedLoadKeyRef = useRef("");
  const isAsyncSearchInFlightRef = useRef(false);
  const latestLoadCallbackRef = useRef(null);
  const latestRenderedOptionsRef = useRef([]);

  const getLoadOptions = useCallback(
    (data, tagsSearchResult, inputValue) => {
      const options = searchUtil.getFormattedSearchResults({
        data,
        tagsSearchResult,
        isHideLabels: isStandardSelect,
        isHideExtraLinks: isStandardSelect,
        searchTerm: inputValue,
        customer,
      });
      return options;
    },
    [customer, isStandardSelect]
  );

  const roleBasedTagTypes = useMemo(() => {
    return roleBasedTagTypeIds(tagTypesConfig, tagsVisibleIn);
  }, [tagTypesConfig, tagsVisibleIn]);

  const debouncedSearchQuery = useMemo(
    () =>
      debounce((scope, inputValue, callback) => {
        const invokeActiveCallbacks = (options) => {
          const callbacks = [callback];
          const latestCallback = latestLoadCallbackRef.current;
          if (latestCallback && latestCallback !== callback) {
            callbacks.push(latestCallback);
          }

          emitSearchDebug("callback.invoke", {
            inputValue,
            callbackCount: callbacks.length,
            groups: (options || []).map((group) => ({
              label: group?.label,
              count: group?.options?.length || 0,
              firstOptionLoading: Boolean(
                group?.options?.[0]?.value?.isLoading
              ),
            })),
          });
          callbacks.forEach((targetCallback) => {
            targetCallback(options);
          });
        };
        const sanitisedQuery = inputValue;
        const requestId = activeSearchRequestRef.current + 1;
        activeSearchRequestRef.current = requestId;

        const queryVariables = {
          size: isForSearchPage ? SEARCH_PAGE_COUNT_LIMIT : SEARCH_COUNT_LIMIT,
          searchTerms: sanitisedQuery,
          includePeople,
          includeGroups,
          includeTags,
          includeEmptyGroups: true,
          filter: {
            ...(!isEmpty(scope) && {
              teams: {
                teams: [scope?.id],
                includeSubTeams: true,
                intersectingTeamMembers: true,
              },
            }),
            tags: {
              tagTypes: roleBasedTagTypes,
            },
          },
        };
        const requestedSections = [
          includePeople && searchUtil.SEARCH_SECTION.PEOPLE,
          includeGroups && searchUtil.SEARCH_SECTION.GROUPS,
          includeTags && searchUtil.SEARCH_SECTION.TAGS,
        ].filter(Boolean);

        if (isEmpty(requestedSections)) {
          setSearchResults([]);
          setSearchData(searchUtil.EMPTY_SEARCH_DATA);
          setIsFetchingAsyncQuery(false);
          isAsyncSearchInFlightRef.current = false;
          setSearchError(null);
          setPrevOptions([]);
          latestRenderedOptionsRef.current = [];
          invokeActiveCallbacks([]);
          return;
        }

        let mergedData = searchUtil.EMPTY_SEARCH_DATA;
        let pendingRequests = requestedSections.length;
        const completedSections = new Set();
        const settledSections = new Set();
        let hasSuccessfulResponse = false;
        let hasDeliveredOptions = false;
        let firstError;

        const updateRenderedResults = (shouldInvokeCallback = false) => {
          const rawOptions = getLoadOptions(mergedData, undefined, inputValue);
          const unresolvedSections =
            shouldInvokeCallback || hasDeliveredOptions
              ? requestedSections.filter(
                  (section) => !settledSections.has(section)
                )
              : [];
          const optionsWithSectionLoading = rawOptions.map((item) => {
            const section = Object.entries(SECTION_LABELS).find(
              ([, label]) => label === item.label
            )?.[0];
            if (
              section &&
              unresolvedSections.includes(section) &&
              isEmpty(item.options)
            ) {
              return {
                ...item,
                options: getSectionPlaceholderOptions(section),
                isSectionLoading: true,
              };
            }
            return item;
          });

          const sectionOptionCounts = optionsWithSectionLoading.reduce(
            (acc, item) => {
              (item?.options || []).forEach((option) => {
                const optionType = option?.value?.type;
                const template = option?.value?.template;
                if (
                  optionType &&
                  template !== "link" &&
                  !option?.value?.isLoading
                ) {
                  acc[optionType] = (acc[optionType] || 0) + 1;
                }
              });
              return acc;
            },
            {}
          );

          const options = filter(optionsWithSectionLoading, (item) => {
            const itemOptions = item?.options || [];
            if (itemOptions.length === 0) {
              return false;
            }

            const firstOption = itemOptions[0];
            const isLinkGroup = firstOption?.value?.template === "link";
            if (!isLinkGroup) {
              return true;
            }

            const linkType = firstOption?.value?.type;
            return Boolean(sectionOptionCounts[linkType]);
          }).sort((left, right) => {
            return (
              Number(Boolean(left?.isSectionLoading)) -
              Number(Boolean(right?.isSectionLoading))
            );
          });
          const dropdownOptions = options.map((item) => {
            const dropdownItem = { ...(item || {}) };
            if (dropdownOptionsDisplayLimit) {
              dropdownItem.options = dropdownItem?.options?.slice(
                0,
                dropdownOptionsDisplayLimit
              );
            }
            return dropdownItem;
          });

          emitSearchDebug("results.update", {
            inputValue,
            shouldInvokeCallback,
            unresolvedSections,
            groups: dropdownOptions.map((group) => ({
              label: group?.label,
              count: group?.options?.length || 0,
              firstOptionLoading: Boolean(
                group?.options?.[0]?.value?.isLoading
              ),
            })),
          });

          setSearchResults(options);
          setSearchData(mergedData);
          setPrevOptions(dropdownOptions);
          latestRenderedOptionsRef.current = dropdownOptions;

          if (shouldInvokeCallback) {
            invokeActiveCallbacks(dropdownOptions);
            hasDeliveredOptions = true;
            // Stop global loading once we have anything to render.
            // Unresolved sections still show row-level placeholders.
            setIsFetchingAsyncQuery(false);
          }

          return options;
        };

        requestedSections.forEach((section) => {
          Promise.resolve(
            client.query({
              query: globalSearchQuery,
              variables: searchUtil.getScopedQueryVariables(
                queryVariables,
                section
              ),
            })
          )
            .then(({ data }) => {
              if (requestId !== activeSearchRequestRef.current) {
                emitSearchDebug("section.drop_stale", {
                  inputValue,
                  section,
                  requestId,
                  activeRequestId: activeSearchRequestRef.current,
                });
                return;
              }

              if (section === searchUtil.SEARCH_SECTION.TAGS) {
                emitSearchDebug("section.tags.success", {
                  inputValue,
                  requestId,
                  tagCount: get(data, "result.pagedTags.tags.length", 0),
                });
              }

              hasSuccessfulResponse = true;
              mergedData = searchUtil.mergeSectionData(
                mergedData,
                section,
                data
              );
              completedSections.add(section);
              settledSections.add(section);

              // Push progressive updates for each section as soon as it resolves,
              // so no section is blocked by the completion order of others.
              updateRenderedResults(true);
            })
            .catch((error) => {
              if (requestId !== activeSearchRequestRef.current) {
                return;
              }
              emitSearchDebug("section.error", {
                inputValue,
                section,
                requestId,
                message: error?.message,
              });

              firstError = firstError || error;
              settledSections.add(section);
              reportError(error);

              if (hasDeliveredOptions) {
                // Drop placeholders for failed sections instead of leaving
                // indefinite skeleton rows in the menu.
                updateRenderedResults(true);
              }
            })
            .finally(() => {
              if (requestId !== activeSearchRequestRef.current) {
                return;
              }

              pendingRequests -= 1;
              if (pendingRequests !== 0) {
                return;
              }

              setIsFetchingAsyncQuery(false);
              isAsyncSearchInFlightRef.current = false;

              if (!hasSuccessfulResponse) {
                setSearchResults([]);
                setSearchData(null);
                setSearchError(firstError);
                setPrevOptions([]);
                latestRenderedOptionsRef.current = [];
                invokeActiveCallbacks([]);
                return;
              }

              // Always emit one final callback once all section requests complete.
              // This guarantees any in-flight placeholders are replaced with final data.
              updateRenderedResults(true);
              lastCompletedLoadKeyRef.current = activeLoadKeyRef.current;

              setSearchError(null);
            });
        });
      }, DEBOUNCE_SEARCH_TIME),
    [
      client,
      dropdownOptionsDisplayLimit,
      getLoadOptions,
      includeGroups,
      includePeople,
      includeTags,
      isForSearchPage,
      roleBasedTagTypes,
      setSearchData,
      setSearchResults,
    ]
  );

  const loadOptions = (inputValue, callback, scope = defaultSearchScope) => {
    latestLoadCallbackRef.current = callback;
    const normalizedInputValue = inputValue || "";
    const loadKey = `${scope?.id || "all"}:${normalizedInputValue}`;

    // AsyncSelect can emit empty-string loadOptions calls while a non-empty
    // request is still in-flight (menu/focus lifecycle). Ignore those so they
    // don't invalidate the active search request and strand section placeholders.
    if (
      isAsyncSearchInFlightRef.current &&
      isEmpty(normalizedInputValue) &&
      !isEmpty(activeInputValueRef.current)
    ) {
      emitSearchDebug("loadOptions.ignore_empty_during_inflight", {
        inputValue: normalizedInputValue,
        activeInputValue: activeInputValueRef.current,
        loadKey,
        activeLoadKey: activeLoadKeyRef.current,
      });
      callback(latestRenderedOptionsRef.current || prevOptions || []);
      return;
    }

    // AsyncSelect can invoke loadOptions multiple times for the same input while a request is still in-flight.
    // Reusing the current options prevents us from restarting the same fan-out request and dropping late section updates.
    if (
      isAsyncSearchInFlightRef.current &&
      (loadKey === activeLoadKeyRef.current ||
        normalizedInputValue === activeInputValueRef.current)
    ) {
      emitSearchDebug("loadOptions.ignore_duplicate", {
        inputValue: normalizedInputValue,
        activeInputValue: activeInputValueRef.current,
        loadKey,
        activeLoadKey: activeLoadKeyRef.current,
      });
      callback(latestRenderedOptionsRef.current || prevOptions || []);
      return;
    }

    if (
      !isAsyncSearchInFlightRef.current &&
      loadKey === lastCompletedLoadKeyRef.current
    ) {
      emitSearchDebug("loadOptions.ignore_already_completed", {
        inputValue: normalizedInputValue,
        loadKey,
      });
      callback(latestRenderedOptionsRef.current || prevOptions || []);
      return;
    }
    activeLoadKeyRef.current = loadKey;
    activeInputValueRef.current = normalizedInputValue;

    if (!isForSearchPage) {
      isAsyncSearchInFlightRef.current = true;
      setIsFetchingAsyncQuery(true);
      emitSearchDebug("loadOptions.dispatch", {
        inputValue: normalizedInputValue,
        loadKey,
        scopeId: scope?.id || "all",
      });
      debouncedSearchQuery(scope, normalizedInputValue, callback);
    }
  };

  const onDefaultScopeClearCallback = (searchQuery) => {
    setDefaultSearchScope(null);
    loadOptions(searchQuery, () => {}, null);
  };

  return (
    <Search
      isForSearchPage={isForSearchPage}
      isMulti={isMulti}
      isCloseOnSelect={isCloseOnSelect}
      isLoading={isFetchingAsyncQuery}
      isStandardSelect={isStandardSelect}
      onOptionSelect={onOptionSelect}
      loadOptions={loadOptions}
      defaultOptions={prevOptions}
      searchError={searchError}
      groupTypes={groupTypes}
      featureFlags={featureFlags}
      workspace={workspace}
      showExploreLink={showExploreLink}
      onDefaultScopeClearCallback={onDefaultScopeClearCallback}
    />
  );
};

SearchContainer.propTypes = {
  isStandardSelect: PropTypes.bool,
  onOptionSelect: PropTypes.func,
  isCloseOnSelect: PropTypes.bool,
  isMulti: PropTypes.bool,
  includePeople: PropTypes.bool,
  includeGroups: PropTypes.bool,
  featureFlags: PropTypes.object,
  workspace: WorkspacePropType,
  showExploreLink: PropTypes.bool,
  tagsVisibleIn: PropTypes.string,
  includeTags: PropTypes.bool,
  dropdownOptionsDisplayLimit: PropTypes.number,
  Search: PropTypes.elementType,
};

// Wrap in react memo so state provider change doesn't re-render component
export default SearchContainer;
